import React from 'react';
import styled from 'styled-components';
import vars from './vars';

/**
 * Small tinted, bordered pill that labels a thing or its state ("Atual", "Cancelado", "Recomendada").
 *
 * `tone` picks the tint:
 *  - `neutral` — plain label, no judgement
 *  - `muted` — something switched off or no longer in effect
 *  - `primary`, `success`, `warning`, `danger` — the usual semantic tints
 *  - `info`, `gold`, `violet` — extra hues for when several badges sit side by side and must stay
 *    distinguishable at a glance, as on the agenda's event cards
 *  - `highlight` — solid brand fill, reserved for promotional callouts such as a free trial
 */
export type BadgeTone =
    | 'neutral'
    | 'muted'
    | 'primary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
    | 'gold'
    | 'violet'
    | 'highlight';

const FILLS: Record<BadgeTone, string> = {
    neutral: vars.backgroundColorDark,
    muted: vars.dividerColor,
    primary: vars.primaryColorLight,
    success: vars.successTint,
    warning: vars.warningTintSoft,
    danger: vars.dangerTint,
    info: '#A0C8E0',
    gold: '#E0D0A0',
    violet: '#C8B0E0',
    highlight: vars.primaryColor,
};

const TEXT_COLORS: Partial<Record<BadgeTone, string>> = {
    muted: '#666',
};

const Pill = styled.span<{ $tone: BadgeTone }>`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid ${(p) => (p.$tone === 'highlight' ? vars.primaryColor : vars.dividerColor)};
    background-color: ${(p) => FILLS[p.$tone]};
    color: ${(p) => TEXT_COLORS[p.$tone] ?? vars.foregroundColor};
    font-size: 0.75rem;
    font-weight: ${(p) => (p.$tone === 'highlight' ? 600 : 400)};
    white-space: nowrap;
`;

type BadgeProps = React.ComponentPropsWithoutRef<'span'> & {
    tone?: BadgeTone;
};

export function Badge({ tone = 'neutral', ...rest }: BadgeProps) {
    return <Pill $tone={tone} {...rest} />;
}
